import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { 
  ArrowLeft, 
  Bookmark, 
  BookmarkX,
  Loader2, 
  MapPin, 
  Trophy, 
  DollarSign,
  ExternalLink,
  Send,
  Trash2,
  GraduationCap,
  Users,
  Globe,
  Star,
  BarChart3,
  FileText,
  AlertTriangle,
  CheckCircle,
  Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

// Minimum required documents for application
const REQUIRED_DOCUMENTS = [
  { type: 'passport', label: 'Passport', required: true },
  { type: 'academic_transcript', label: 'Academic Transcript', required: true },
  { type: 'english_test', label: 'English Test Score (IELTS/PTE/TOEFL)', required: true },
];

const SavedUniversitiesPage = () => {
  const { isDark } = useTheme();
  const { user, isAuthenticated, loading: authLoading } = useStudentAuth();
  const navigate = useNavigate();
  
  const [savedUniversities, setSavedUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUniversities, setSelectedUniversities] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  
  // Document check state
  const [userDocuments, setUserDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [pendingApplication, setPendingApplication] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/uac-desk/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchSavedUniversities = async () => {
      if (!user?.user_id) return;
      
      try {
        const res = await fetch(
          `${window.location.origin}/api/university-pages/saved/${user.user_id}`,
          { credentials: 'include' }
        );
        
        if (res.ok) {
          const data = await res.json();
          setSavedUniversities(data.saved_universities || []);
        }
      } catch (error) {
        console.error('Error fetching saved universities:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSavedUniversities();
  }, [user]);

  // Fetch user's uploaded documents
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user?.user_id) return;
      
      try {
        const res = await fetch(
          `${window.location.origin}/api/auth/documents/${user.user_id}`,
          { credentials: 'include' }
        );
        
        if (res.ok) {
          const data = await res.json();
          setUserDocuments(data.documents || []);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };
    
    fetchDocuments();
  }, [user]);

  // Check if user has all required documents
  const checkRequiredDocuments = () => {
    const uploadedTypes = userDocuments.map(doc => doc.document_type?.toLowerCase() || doc.type?.toLowerCase());
    const missing = REQUIRED_DOCUMENTS.filter(req => 
      req.required && !uploadedTypes.some(type => 
        type?.includes(req.type) || req.type.includes(type)
      )
    );
    return { hasMissing: missing.length > 0, missing };
  };

  // Handle apply with document check
  const handleApplyWithCheck = (university) => {
    const { hasMissing, missing } = checkRequiredDocuments();
    
    if (hasMissing) {
      setPendingApplication(university);
      setShowDocumentModal(true);
    } else {
      proceedWithApplication(university);
    }
  };

  // Proceed with application after document check
  const proceedWithApplication = async (university) => {
    try {
      await fetch(`${window.location.origin}/api/applications/create/${user.user_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          university_name: university.university_name,
          program: 'General Program',
          intake: 'Feb/July 2026',
          country: university.country,
          notes: 'Applied from Saved Universities'
        })
      });
      toast.success(`Applied to ${university.university_name}!`);
      navigate('/uac-desk/applications');
    } catch (error) {
      toast.error('Failed to apply');
    }
  };

  const handleRemove = async (slug) => {
    if (!user?.user_id) return;
    
    setRemoving(slug);
    try {
      const res = await fetch(
        `${window.location.origin}/api/university-pages/saved/${user.user_id}/${slug}`,
        { method: 'DELETE', credentials: 'include' }
      );
      
      if (res.ok) {
        setSavedUniversities(prev => prev.filter(u => u.slug !== slug));
        setSelectedUniversities(prev => {
          const newSet = new Set(prev);
          newSet.delete(slug);
          return newSet;
        });
        toast.success('Removed from saved universities');
      }
    } catch (error) {
      console.error('Error removing university:', error);
      toast.error('Failed to remove');
    } finally {
      setRemoving(null);
    }
  };

  const toggleSelection = (slug) => {
    setSelectedUniversities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slug)) {
        newSet.delete(slug);
      } else {
        if (newSet.size >= 5) {
          toast.error('You can select up to 5 universities at a time');
          return prev;
        }
        newSet.add(slug);
      }
      return newSet;
    });
  };

  const handleApply = async () => {
    if (selectedUniversities.size === 0) {
      toast.error('Please select at least one university');
      return;
    }
    
    // Check for required documents first
    const { hasMissing } = checkRequiredDocuments();
    if (hasMissing) {
      setPendingApplication({ bulk: true });
      setShowDocumentModal(true);
      return;
    }
    
    setApplying(true);
    try {
      const selectedUnis = savedUniversities.filter(u => selectedUniversities.has(u.slug));
      
      for (const uni of selectedUnis) {
        await fetch(`${window.location.origin}/api/applications/create/${user.user_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            university_name: uni.university_name,
            program: 'General Program',
            intake: 'Feb/July 2026',
            country: uni.country,
            notes: 'Applied from Saved Universities'
          })
        });
      }
      
      toast.success(`Applied to ${selectedUnis.length} universities!`);
      setSelectedUniversities(new Set());
      
      setTimeout(() => {
        navigate('/uac-desk/applications');
      }, 1500);
    } catch (error) {
      console.error('Error applying:', error);
      toast.error('Failed to submit applications');
    } finally {
      setApplying(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen w-full max-w-[100vw] overflow-x-hidden ${isDark ? 'bg-[#1a2830]' : 'bg-ux-slate-light'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b w-full ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-ux-slate-light'}`}>
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/uac-desk/dashboard')} className="flex-shrink-0">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            
            {selectedUniversities.size > 0 && (
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                  {selectedUniversities.size} selected
                </span>
                <Button 
                  variant="outline"
                  onClick={() => setShowCompareModal(true)}
                  className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Compare
                </Button>
                <Button 
                  onClick={handleApply}
                  disabled={applying}
                  className="bg-gradient-to-r from-ux-violet to-purple-600"
                >
                  {applying ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Apply
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Bookmark className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                Saved Universities
              </h1>
              <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                {savedUniversities.length} universities saved for later
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {savedUniversities.length === 0 ? (
          <Card className={isDark ? 'bg-[#233842] border-white/10' : ''}>
            <CardContent className="p-12 text-center">
              <BookmarkX className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/30' : 'text-ux-charcoal/30'}`} />
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                No saved universities yet
              </h3>
              <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                Save universities from AI Finder or University Browser to compare them later
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={() => navigate('/uac-desk/ai-finder')} className="bg-gradient-to-r from-ux-cyan to-ux-blue">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  AI University Finder
                </Button>
                <Button variant="outline" onClick={() => navigate('/uac-desk/browse-universities')}>
                  Browse Universities
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Selection Info */}
            <div className={`mb-6 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-blue-50'}`}>
              <p className={`text-sm ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                Select universities to apply to multiple at once, or click "Apply Now" on individual cards.
              </p>
            </div>

            {/* Universities Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {savedUniversities.map((uni) => (
                <Card 
                  key={uni.slug}
                  className={`relative transition-all ${
                    selectedUniversities.has(uni.slug) 
                      ? 'ring-2 ring-ux-violet' 
                      : ''
                  } ${isDark ? 'bg-[#233842] border-white/10' : ''}`}
                >
                  <CardContent className="p-5">
                    {/* Selection Checkbox */}
                    <div className="absolute top-4 right-4">
                      <Checkbox
                        checked={selectedUniversities.has(uni.slug)}
                        onCheckedChange={() => toggleSelection(uni.slug)}
                      />
                    </div>

                    {/* University Info */}
                    <h3 className={`text-lg font-bold mb-2 pr-8 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      {uni.university_name}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {uni.country && (
                        <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full ${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-ux-charcoal/70'}`}>
                          <MapPin className="w-3 h-3 mr-1" />
                          {uni.country}
                        </span>
                      )}
                      {uni.world_ranking && (
                        <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                          <Trophy className="w-3 h-3 mr-1" />
                          #{uni.world_ranking}
                        </span>
                      )}
                    </div>

                    {uni.tagline && (
                      <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                        {uni.tagline}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleRemove(uni.slug)}
                        disabled={removing === uni.slug}
                      >
                        {removing === uni.slug ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </>
                        )}
                      </Button>
                      <Link to={`/universities/${uni.slug}`} target="_blank" className="flex-1">
                        <Button size="sm" variant="outline" className="w-full">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Apply Button */}
                    <Button
                      size="sm"
                      className="w-full mt-2 bg-gradient-to-r from-ux-violet to-purple-600"
                      onClick={() => handleApplyWithCheck(uni)}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Apply Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Compare Modal */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className={`max-w-6xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a2830] border-white/10' : 'bg-white'}`}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className={`text-xl ${isDark ? 'text-white' : ''}`}>
                  Compare Universities
                </DialogTitle>
                <DialogDescription className={isDark ? 'text-white/60' : ''}>
                  Side-by-side comparison of {selectedUniversities.size} selected universities
                </DialogDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowCompareModal(false)}
                className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </DialogHeader>
          
          {/* Comparison Table */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <th className={`text-left p-3 font-medium ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    Attribute
                  </th>
                  {savedUniversities.filter(u => selectedUniversities.has(u.slug)).map((uni, idx) => (
                    <th key={idx} className={`text-center p-3 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm">{uni.university_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`}>
                          {uni.country}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Ranking Row */}
                <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      World Ranking
                    </div>
                    <span className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>(QS 2027)</span>
                  </td>
                  {savedUniversities.filter(u => selectedUniversities.has(u.slug)).map((uni, idx) => (
                    <td key={idx} className={`text-center p-3 font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      {uni.world_ranking ? `#${String(uni.world_ranking).replace(/[^0-9-]/g, '')}` : 'N/A'}
                    </td>
                  ))}
                </tr>
                {/* Country Row */}
                <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-ux-cyan" />
                      Country
                    </div>
                  </td>
                  {savedUniversities.filter(u => selectedUniversities.has(u.slug)).map((uni, idx) => (
                    <td key={idx} className={`text-center p-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {uni.country || 'N/A'}
                    </td>
                  ))}
                </tr>
                {/* Student Population Row */}
                <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-ux-violet" />
                      Students
                    </div>
                  </td>
                  {savedUniversities.filter(u => selectedUniversities.has(u.slug)).map((uni, idx) => (
                    <td key={idx} className={`text-center p-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {uni.student_population ? String(uni.student_population).match(/([\d,]+\+?)/)?.[1] || uni.student_population : 'N/A'}
                    </td>
                  ))}
                </tr>
                {/* International Students Row */}
                <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <td className={`p-3 font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-green-500" />
                      International %
                    </div>
                  </td>
                  {savedUniversities.filter(u => selectedUniversities.has(u.slug)).map((uni, idx) => (
                    <td key={idx} className={`text-center p-3 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      {uni.international_students ? (() => {
                        const intl = String(uni.international_students);
                        const match = intl.match(/(\d+[-–]?\d*)\s*%/);
                        return match ? `${match[1]}%` : intl.split(/[(\s]/)[0];
                      })() : 'N/A'}
                    </td>
                  ))}
                </tr>
                {/* Acceptance Rate row removed — no verified source available */}
                {/* Tagline/Description Row */}
                <tr className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                  <td className={`p-3 font-medium align-top ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-purple-400" />
                      About
                    </div>
                  </td>
                  {savedUniversities.filter(u => selectedUniversities.has(u.slug)).map((uni, idx) => (
                    <td key={idx} className={`p-3 align-top text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      {uni.tagline || uni.meta_description || 'No description available'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className={`mt-4 p-3 rounded-lg text-center text-xs ${isDark ? 'bg-white/5 text-white/50' : 'bg-gray-50 text-gray-500'}`}>
            Rankings source: QS World University Rankings 2025. Statistics are estimates and may vary.
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCompareModal(false)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Saved
            </Button>
            <Button 
              onClick={() => {
                setShowCompareModal(false);
                handleApply();
              }}
              disabled={applying}
              className="bg-gradient-to-r from-ux-violet to-purple-600"
            >
              <Send className="w-4 h-4 mr-2" />
              Apply to Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Check Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#1a2830] border-white/10' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Documents Required
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-white/60' : ''}>
              Please upload the following documents before applying:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 my-4">
            {REQUIRED_DOCUMENTS.map((doc) => {
              const uploadedTypes = userDocuments.map(d => d.document_type?.toLowerCase() || d.type?.toLowerCase());
              const isUploaded = uploadedTypes.some(type => 
                type?.includes(doc.type) || doc.type.includes(type)
              );
              
              return (
                <div 
                  key={doc.type}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isUploaded 
                      ? isDark ? 'bg-emerald-500/20' : 'bg-emerald-50'
                      : isDark ? 'bg-red-500/20' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className={`w-5 h-5 ${isUploaded ? 'text-emerald-500' : 'text-red-500'}`} />
                    <span className={isDark ? 'text-white' : 'text-gray-900'}>{doc.label}</span>
                  </div>
                  {isUploaded ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <span className="text-xs text-red-500 font-medium">Missing</span>
                  )}
                </div>
              );
            })}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDocumentModal(false);
                setPendingApplication(null);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowDocumentModal(false);
                // Save pending application info to localStorage
                if (pendingApplication) {
                  localStorage.setItem('pending_application', JSON.stringify(pendingApplication));
                }
                navigate('/uac-desk/documents');
              }}
              className="w-full sm:w-auto bg-gradient-to-r from-ux-cyan to-ux-blue"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Documents
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedUniversitiesPage;
